import { useState, useCallback } from "react";

export const SORTABLE_COLUMNS = [
  { key: "originalName", label: "Documento", type: "text" },
  { key: "obra", label: "Obra", type: "text" },
  { key: "documentType", label: "Tipo", type: "text" },
  { key: "status", label: "Estado", type: "status" },
  { key: "createdAt", label: "Creado", type: "date" },
  { key: "signedAt", label: "Firmado", type: "date" },
  { key: "signerName", label: "Firmante", type: "text" },
];

const validColumnKeys = new Set(SORTABLE_COLUMNS.map((c) => c.key));
const columnTypeMap = Object.fromEntries(SORTABLE_COLUMNS.map((c) => [c.key, c.type]));

function toTimestamp(val) {
  if (!val) return 0;
  if (typeof val.toMillis === "function") return val.toMillis();
  if (typeof val.seconds === "number") return val.seconds * 1000;
  return 0;
}

function compareDate(a, b) {
  return toTimestamp(a) - toTimestamp(b);
}

function compareText(a, b) {
  const sa = (a || "").toString().toLowerCase();
  const sb = (b || "").toString().toLowerCase();
  return sa.localeCompare(sb);
}

export function deriveVisualStatus(item) {
  if (item.status === "signed") return "firmado";
  if (item.expiresAt && typeof item.expiresAt.toDate === "function") {
    if (item.expiresAt.toDate() < new Date()) return "expirado";
  }
  return "pendiente";
}

function compareStatus(a, b) {
  return compareText(deriveVisualStatus(a), deriveVisualStatus(b));
}

export function useSortableTable({
  defaultColumn = "createdAt",
  defaultDirection = "desc",
} = {}) {
  const [sortColumn, setSortColumn] = useState(defaultColumn);
  const [sortDirection, setSortDirection] = useState(defaultDirection);

  const handleSort = useCallback(
    (columnKey) => {
      if (!validColumnKeys.has(columnKey)) return;
      if (columnKey === sortColumn) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortColumn(columnKey);
        setSortDirection("asc");
      }
    },
    [sortColumn]
  );

  const sortItems = useCallback(
    (items) => {
      if (!items || items.length === 0) return [];
      const type = columnTypeMap[sortColumn];
      const dir = sortDirection === "asc" ? 1 : -1;
      return [...items].sort((a, b) => {
        let result;
        if (type === "date") {
          result = compareDate(a[sortColumn], b[sortColumn]);
        } else if (type === "status") {
          result = compareStatus(a, b);
        } else {
          result = compareText(a[sortColumn], b[sortColumn]);
        }
        return result * dir;
      });
    },
    [sortColumn, sortDirection]
  );

  return { sortColumn, sortDirection, handleSort, sortItems };
}
